/**
 * Site audits API client: every public audit read as one desk.
 *
 * Mirrors `apps/api/src/modules/crm-siteaudit/admin.routes.ts`. One row per
 * DOMAIN with a column per audit, because the question the desk exists for is
 * about the SET - a site audited for agent readiness but never for compliance
 * is a prospect who already gave us part of a diagnosis and an address.
 *
 * The row carries a `Record<AuditKind, ...>` rather than one field per audit.
 * It was a pair of named fields until a third audit arrived, and every screen
 * that read them had to be edited by hand. Now the table walks `AUDIT_KINDS`.
 */
import { api } from "./api";

/** Which audit produced a row. Mirrors the server's `AuditKind`. */
export type AuditKind = "aeo" | "seo" | "gdpr";

/** Display order for the columns, the tiles and the filter. */
export const AUDIT_KINDS: AuditKind[] = ["aeo", "seo", "gdpr"];

/**
 * How the desk is narrowed.
 *
 * `missing-<kind>` is the one an operator actually reaches for, because it is
 * the next call to make. These are statements about the whole set of audits a
 * domain has, so the server applies them after folding the rows, and a filtered
 * page can come back shorter than the page size - see `filteredOut`.
 */
export type AuditCoverage = "all" | "complete" | "missing-aeo" | "missing-seo" | "missing-gdpr";

export type AuditStatus = "all" | "leads";

/** One audit of one kind, as the table shows it. */
export interface AuditCell {
  id: string;
  score: number;
  groups: Record<string, number>;
  at: string;
  /** Defects only. Good news is a finding too, and it does not belong in a count. */
  issues: number;
  email: string | null;
  leadId: string | null;
  /** Absolute URL of the shareable report, on the audited site's own domain. */
  reportUrl: string;
  runs: number;
}

export interface AuditDeskRow {
  domain: string;
  lastAt: string;
  /** One entry per kind, always present, null where that audit has not run. */
  audits: Record<AuditKind, AuditCell | null>;
}

export interface AuditTotals {
  domains: number;
  /** Domains that have had each audit at least once. */
  byKind: Record<AuditKind, number>;
  /** Domains that have had every audit. */
  complete: number;
  /**
   * A ROW count, unlike every number beside it: a domain that unlocked two
   * audits gave an address twice and is counted twice.
   */
  leads: number;
}

export interface AuditDeskPage {
  rows: AuditDeskRow[];
  page: number;
  limit: number;
  hasMore: boolean;
  /** How many domains this page's coverage filter removed. Named, never silent. */
  filteredOut?: number;
  totals: AuditTotals;
}

export interface AuditFilters {
  q: string;
  coverage: AuditCoverage;
  status: AuditStatus;
}

export const EMPTY_AUDIT_FILTERS: AuditFilters = { q: "", coverage: "all", status: "all" };

/** One audit in a domain's full history. */
export interface AuditHistoryItem {
  id: string;
  kind: AuditKind;
  score: number;
  groups: Record<string, number>;
  findings: Array<{
    id: string;
    group: string;
    severity: "error" | "warning" | "info" | "good";
    weight: number;
    title: string;
    body: string;
    fix?: string;
    evidence?: string;
  }>;
  url: string;
  email: string | null;
  name: string | null;
  leadId: string | null;
  token: string;
  openCount: number;
  sourceCampaign: string | null;
  createdAt: string;
  unlockedAt: string | null;
  reportUrl: string;
}

function toQuery(params: Record<string, string | number | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === "") continue;
    q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}

export function listSiteAudits(
  filters: AuditFilters,
  page: number,
  signal?: AbortSignal,
): Promise<AuditDeskPage> {
  return api.get<AuditDeskPage>(
    `/admin/site-audits${toQuery({
      q: filters.q.trim() || undefined,
      coverage: filters.coverage === "all" ? undefined : filters.coverage,
      status: filters.status === "all" ? undefined : filters.status,
      page,
    })}`,
    signal,
  );
}

export function siteAuditHistory(
  domain: string,
  signal?: AbortSignal,
): Promise<{ domain: string; audits: AuditHistoryItem[] }> {
  return api.get(`/admin/site-audits/${encodeURIComponent(domain)}`, signal);
}

/**
 * A score's band.
 *
 * Three bands rather than a gradient, the same choice the public report makes:
 * a colour that shifts by one point per point reads as precision this
 * measurement does not have.
 */
export function auditBand(score: number): "bad" | "mid" | "good" {
  if (score >= 80) return "good";
  if (score >= 50) return "mid";
  return "bad";
}
